import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import {
    localeHe as localeHeMobiscroll,
    MbscDatepicker,
    MbscDateType,
    MbscEventcalendarOptions,
} from "@mobiscroll/angular";
import * as moment from "moment";
import { Duration } from "moment";
import { HttpServiceDiary } from "src/app/services/Diary/http/http.service_Diary";
import { Config, ConfigDiary } from "src/classes/Common/ConfigData";
import { AppointmentData, InvalidResourceTime, NextSlotIn } from "src/classes/Diary/Appointment";

type Resource = {
    id: number;
    name: string;
    imagePath: string;
};

@Component({
    selector: "app-create-app-absence",
    templateUrl: "./create-app-absence.component.html",
    styleUrls: ["./create-app-absence.component.css"],
})
export class CreateAppAbsenceComponent {
    @Input() config: Config;
    @Input() configDiary: ConfigDiary;
    @Input() appDataTemp: AppointmentData;
    @Input() holidayName: string;
    @Input() resources: Resource[];
    @Input() invalid: InvalidResourceTime[];
    @Input() isEditingEvent: boolean;

    @Output() eventTitleChange: EventEmitter<string> = new EventEmitter<string>();
    @Output() isEditHoursChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild("timepicker", { static: false }) timepicker: MbscDatepicker;
    @ViewChild("absenceStep", { static: false }) absenceStep: ElementRef;

    startInvalid: InvalidResourceTime[] = [];
    endInvalid: InvalidResourceTime[] = [];

    startTime: string;
    endTime: string;
    eventDuration: string;

    isEndTimeSettedNullNow: boolean = false;

    startTimepickerValue: MbscDateType;
    endTimepickerValue: MbscDateType;

    activeTimepicker: "start" | "end" = "start";

    isTimeEditing: boolean = false;

    selectedResource: Resource;

    isResourceListOpened: boolean = false;

    // Guard to ignore initial onChange from Mobiscroll picker
    private ignoreNextTimeChange: boolean = false;

    options: MbscEventcalendarOptions = {
        locale: localeHeMobiscroll,
        theme: "windows",
        themeVariant: "light",
    };

    constructor(private httpService: HttpServiceDiary) {}

    // Function that works on the opening of this step
    initialize(): void {
        this.isResourceListOpened = false;

        this.isTimeEditing = false;
        this.isEditHoursChange.emit(false);

        this.selectedResource = (this.resources as Resource[]).find(
            (resource: Resource) => resource.id == this.appDataTemp.resourceId
        );

        this.eventTitleChange.emit("הפסקה");

        if (!this.isEditingEvent) {
            this.appDataTemp.absenceId = 2;
            this.appDataTemp.remark = "";
        }

        if (this.appDataTemp.absenceId == null) {
            this.appDataTemp.absenceId = 2;
        }

        this.appDataTemp.statusId = null;
        this.appDataTemp.appointorId = null;
        this.appDataTemp.subserv = [];

        this.startTime = moment(this.appDataTemp.start as Date).format("HH:mm");
        this.endTime = moment(this.appDataTemp.end as Date).format("HH:mm");

        // Filter the invalid times for the current resource and create the start and end invalid times
        this.invalid = this.invalid.filter((item: InvalidResourceTime) => item.resource == this.appDataTemp.resourceId);

        this.startInvalid = this.invalid.map((item: InvalidResourceTime) => ({
            start: item.start,
            end: moment(item.end, "HH:mm").add(-this.configDiary.calScaleInMin, "minute").format("HH:mm"),
            resource: item.resource,
        }));

        this.startInvalid.unshift({
            start: "00:00",
            end: moment(this.configDiary.fromHour, "HH:mm")
                .add(-this.configDiary.calScaleInMin, "minute")
                .format("HH:mm"),
            resource: this.appDataTemp.resourceId as number,
        });

        this.startInvalid.push({
            start: this.configDiary.toHour,
            end: "23:59",
            resource: this.appDataTemp.resourceId as number,
        });

        this.endInvalid.unshift({
            start: "00:00",
            end: this.startTime,
            resource: this.appDataTemp.resourceId as number,
        });

        // Find the first invalid time that is after the start time, if it does not exist, then the end time is the end of the working day
        for (let i = 0; i < this.invalid.length; i++) {
            const item: InvalidResourceTime = this.invalid[i];

            if (moment(item.start, "HH:mm").isAfter(moment(this.startTime, "HH:mm"))) {
                this.endInvalid.push({
                    start: moment(item.start, "HH:mm").add(this.configDiary.calScaleInMin, "minute").format("HH:mm"),
                    end: "23:59",
                    resource: this.appDataTemp.resourceId as number,
                });

                break;
            }
        }

        if (this.endInvalid.length == 1) {
            this.endInvalid.push({
                start: moment(this.configDiary.toHour, "HH:mm")
                    .add(this.configDiary.calScaleInMin, "minute")
                    .format("HH:mm"),
                end: "23:59",
                resource: this.appDataTemp.resourceId as number,
            });
        }

        const duration: Duration = moment.duration(moment(this.appDataTemp.end as Date).diff(this.appDataTemp.start));

        this.eventDuration = duration.hours().toString() + " ש׳ " + duration.minutes().toString() + " דק׳";

        this.startTimepickerValue = this.startTime;
        this.endTimepickerValue = this.endTime;

        let [hours, minutes] = this.startTime.split(":").map(Number);
        (this.appDataTemp.start as Date).setHours(hours, minutes, 0);

        [hours, minutes] = this.endTime.split(":").map(Number);
        (this.appDataTemp.end as Date).setHours(hours, minutes, 0);
    }

    // Function that selects the reason for absence
    chooseAbsence(absenceId: number): void {
        this.appDataTemp.absenceId = absenceId;

        if (absenceId == 1) {
            this.eventTitleChange.emit("חופש");
        } else if (absenceId == 2) {
            this.eventTitleChange.emit("הפסקה");
        } else if (absenceId == 3) {
            this.eventTitleChange.emit("זמן אישי");
        }
    }

    // Function that opens the timepicker
    editTime(): void {
        this.startTimepickerValue = this.startTime;
        this.endTimepickerValue = this.endTime;

        // Ignore the initial onChange that Mobiscroll fires when picker renders
        this.ignoreNextTimeChange = true;

        this.chooseStartOrEndTimepicker("start");

        this.absenceStep.nativeElement.scrollTo({ top: 150, behavior: "smooth" });

        this.isTimeEditing = true;
        this.isEditHoursChange.emit(true);
    }

    // Sets the end time to null in the timepicker
    setEndTimeNull(): void {
        this.endTime = "בחר";
        this.endTimepickerValue = null;
        this.isEndTimeSettedNullNow = true;
    }

    // Function change the time selection mode, that indicates which timepicker is active
    chooseStartOrEndTimepicker(mode: "start" | "end"): void {
        // Ignore initial onChange when switching pickers
        this.ignoreNextTimeChange = true;
        this.activeTimepicker = mode;

        if (this.activeTimepicker == "end") {
            this.endInvalid = [
                {
                    start: "00:00",
                    end: this.startTime,
                    resource: this.appDataTemp.resourceId as number,
                },
            ];

            // Find the first invalid time that is after the start time, if it does not exist, then the end time is the end of the working day
            for (let i = 0; i < this.invalid.length; i++) {
                const item: InvalidResourceTime = this.invalid[i];

                if (moment(item.start, "HH:mm").isAfter(moment(this.startTime, "HH:mm"))) {
                    this.endInvalid.push({
                        start: moment(item.start, "HH:mm")
                            .add(this.configDiary.calScaleInMin, "minute")
                            .format("HH:mm"),
                        end: "23:59",
                        resource: this.appDataTemp.resourceId as number,
                    });

                    break;
                }
            }

            if (this.endInvalid.length == 1) {
                this.endInvalid.push({
                    start: moment(this.configDiary.toHour, "HH:mm")
                        .add(this.configDiary.calScaleInMin, "minute")
                        .format("HH:mm"),
                    end: "23:59",
                    resource: this.appDataTemp.resourceId as number,
                });
            }

            // If the end time is setted to null, then the end time will be the start time + the minimum duration of the event
            if (this.isEndTimeSettedNullNow) {
                this.endTimepickerValue = moment(this.startTime, "HH:mm")
                    .add(this.configDiary.calScaleInMin, "minute")
                    .format("HH:mm");
                this.endTime = this.endTimepickerValue as string;
                this.isEndTimeSettedNullNow = false;
            }

            // If the maximum end time is before the end timepicker value, then the end timepicker value will be the maximum end time
            if (
                moment(this.endInvalid[this.endInvalid.length - 1].start, "HH:mm").isBefore(
                    typeof this.endTimepickerValue == "string"
                        ? moment(this.endTimepickerValue, "HH:mm")
                        : moment(this.endTimepickerValue)
                )
            ) {
                this.endTimepickerValue = this.endInvalid[this.endInvalid.length - 1].start;
                this.endTime = this.endTimepickerValue as string;
            }
        }
    }

    // Function that works on the change of time
    onTimeChange(mode: "start" | "end"): void {
        // Skip initial onChange fired by Mobiscroll when picker renders
        if (this.ignoreNextTimeChange) {
            this.ignoreNextTimeChange = false;
            // Restore the correct value if Mobiscroll changed it
            if (mode === "start" && typeof this.startTimepickerValue !== "string") {
                this.startTimepickerValue = this.startTime;
            } else if (mode === "end" && typeof this.endTimepickerValue !== "string") {
                this.endTimepickerValue = this.endTime;
            }
            return;
        }

        if (mode == "start") {
            if (typeof this.startTimepickerValue == "string") {
                this.startTime = this.startTimepickerValue;
            } else {
                // If the start time is after the end time, then the end time will be setted to null
                if (
                    moment(this.startTimepickerValue).isSameOrAfter(
                        moment(
                            typeof this.endTimepickerValue == "string"
                                ? moment(this.endTimepickerValue, "HH:mm")
                                : moment(this.endTimepickerValue)
                        )
                    )
                ) {
                    this.setEndTimeNull();
                }

                // Check if the chosen start time does not intersect with the invalid times
                for (let i = 0; i < this.invalid.length; i++) {
                    const item: InvalidResourceTime = this.invalid[i];

                    if (
                        moment(item.start, "HH:mm").isBefore(
                            moment(
                                typeof this.endTimepickerValue == "string"
                                    ? moment(this.endTimepickerValue, "HH:mm")
                                    : moment(this.endTimepickerValue)
                            )
                        ) &&
                        moment(item.end, "HH:mm").isAfter(moment(this.startTimepickerValue))
                    ) {
                        this.endTimepickerValue = item.start as string;
                        this.endTime = this.endTimepickerValue;
                        break;
                    }
                }

                this.startTime = moment(this.startTimepickerValue).format("HH:mm");
            }
        } else if (mode == "end") {
            if (typeof this.endTimepickerValue == "string") {
                this.endTime = this.endTimepickerValue;
            } else {
                this.endTime = moment(this.endTimepickerValue).format("HH:mm");
            }
        }
    }

    // Function that saves changes in time
    saveEditedTime(): void {
        // If the end time is setted to null, then restore the previous values of the start and end times
        if (this.isEndTimeSettedNullNow) {
            this.startTime = moment(this.appDataTemp.start as Date).format("HH:mm");
            this.endTime = moment(this.appDataTemp.end as Date).format("HH:mm");
        } else {
            this.startTime =
                typeof this.startTimepickerValue == "string"
                    ? this.startTimepickerValue
                    : moment(this.startTimepickerValue).format("HH:mm");
            this.endTime =
                typeof this.endTimepickerValue == "string"
                    ? this.endTimepickerValue
                    : moment(this.endTimepickerValue).format("HH:mm");
        }

        const duration: moment.Duration = moment.duration(
            moment(this.endTime as string, "HH:mm").diff(moment(this.startTime, "HH:mm"))
        );

        this.eventDuration = duration.hours().toString() + " ש׳ " + duration.minutes().toString() + " דק׳";

        let [hours, minutes] = this.startTime.split(":").map(Number);
        (this.appDataTemp.start as Date).setHours(hours, minutes, 0);

        [hours, minutes] = this.endTime.split(":").map(Number);
        (this.appDataTemp.end as Date).setHours(hours, minutes, 0);

        this.startTimepickerValue = this.startTime;
        this.endTimepickerValue = this.endTime;

        const nextslotin: NextSlotIn = new NextSlotIn();
        nextslotin.saparId = this.appDataTemp.resourceId;
        nextslotin.startEvent = moment(this.appDataTemp.start).format("MM/DD/YYYY, HH:mm");

        this.httpService.getStartNextSlot(nextslotin).subscribe((data: string) => {
            this.appDataTemp.maxEventEndTime = data;
        });

        this.isTimeEditing = false;
        this.isEditHoursChange.emit(false);
    }

    // Function that cancels the time change and closes the timepicker
    closeEditingTime(): void {
        this.startTimepickerValue = moment(this.appDataTemp.start as Date).format("HH:mm");
        this.endTimepickerValue = moment(this.appDataTemp.end as Date).format("HH:mm");

        this.startTime = this.startTimepickerValue;
        this.endTime = this.endTimepickerValue;

        this.isEndTimeSettedNullNow = false;

        this.isTimeEditing = false;
        this.isEditHoursChange.emit(false);
    }

    // Function that opens a resource dropdown
    toggleResourceList(): void {
        this.isResourceListOpened = !this.isResourceListOpened;
    }

    // Returns a link on the resource image
    getResourceImage(imagePath: string): string {
        if (this.config) {
            return (
                "https://optimusb12023.s3.eu-north-1.amazonaws.com/" + this.config.remotebusid + "/team/" + imagePath
            );
        }
    }

    // Function that saves changes to the resource dropdown
    chooseResource(resource: Resource): void {
        this.appDataTemp.resourceId = resource.id;
        this.selectedResource = resource;
        this.toggleResourceList();
    }
}
